import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { environment } from '../../../../environments/environment';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

export interface AccordionLink {
  id?: string | number | null | any;
  label: string;
  url: string;
  type?: string;        // 'FILES' | 'LINKS' | 'TEXT'
  description?: string;
  icon?: string;
  disabled?: boolean;
  content?: string;     // full text body for TEXT type
}

export interface MenuAction {
  action: string;
  label: string;
  icon: string;
  disabled?: boolean;
}

export interface AccordionItem {
  id?: string;
  title: string;
  type: 'links' | 'accordion' | 'custom';
  description?: string;
  icon?: string;
  expanded?: boolean;
  disabled?: boolean;
  loading?: boolean;
  badge?: string | number;
  actions?: boolean;
  actionIcon?: string;
  actionTooltip?: string;
  links?: AccordionLink[];
  children?: AccordionItem[];
  customTemplate?: TemplateRef<any>;
  data?: any;
  menuActions?: MenuAction[];
  selected?: boolean;
}

@Component({
  selector: 'app-expansion-panel',
  standalone: true,
  templateUrl: './expansion-panel.component.html',
  styleUrls: ['./expansion-panel.component.scss'],
  imports: [
    CommonModule,
    MatExpansionModule, // <mat-accordion>, <mat-expansion-panel>
    MatTooltipModule, // matTooltip
    MatButtonModule, // mat-button
    MatMenuModule, // mat-menu
    MatDividerModule, // mat-divider
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DragDropModule,
  ],
  host: {
    '[class]': "'nested-level-' + nestedLevel",
  },
})
export class ExpansionPanelComponent implements OnChanges {
  @Input() items: AccordionItem[] | any = [];
  @Input() nestedLevel: number = 0;
  @Input() selectedId: any;

  @Output() itemExpanded = new EventEmitter<AccordionItem>();
  @Output() itemCollapsed = new EventEmitter<AccordionItem>();
  @Output() linkClicked = new EventEmitter<AccordionLink>();
  @Output() actionClicked = new EventEmitter<{
    action: string;
    item: AccordionItem;
  }>();
  @Output() filesReordered = new EventEmitter<{
    item: AccordionItem;
    links: AccordionLink[];
  }>();

  accordionItems: AccordionItem[] = [];

  // Per-accordion-item reorder state (keyed by item.id)
  reorderModeMap = new Map<string, boolean>();
  isLoadingMap = new Map<string, boolean>();
  initialOrderMap = new Map<string, any[]>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      // Fresh data arrived from the API — clear all reorder state so no
      // stale drag handles or loading spinners linger after a reload.
      this.reorderModeMap.clear();
      this.isLoadingMap.clear();
      this.initialOrderMap.clear();

      if (this.items && !Array.isArray(this.items) && this.items.rows) {
        this.accordionItems = this.transformApiResponse(this.items.rows);
      } else if (Array.isArray(this.items)) {
        this.accordionItems = [...this.items];
      }
    }
  }

  private transformApiResponse(rows: any[]): AccordionItem[] {
    return rows.map((apiItem) => {
      const item: AccordionItem = {
        id: apiItem.id?.toString(),
        title: apiItem.title || apiItem.name || 'Untitled',
        description: apiItem.description || '',
        type: apiItem.type || 'links',
        expanded: apiItem.expanded ?? false,
        disabled: apiItem.disabled ?? false,
        actions: true,
        icon: apiItem.icon || 'fas fa-folder',
        badge: apiItem.badge || '',
        data: apiItem,
      };
      if (
        apiItem.children &&
        Array.isArray(apiItem.children) &&
        apiItem.children.length > 0
      ) {
        item.type = 'accordion';
        item.children = this.transformApiResponse(apiItem.children);
      } else if (
        apiItem.accordion_content &&
        Array.isArray(apiItem.accordion_content)
      ) {
        item.type = 'links';
        item.links = apiItem.accordion_content.map((file: any) => ({
          id: file.accordion_details_id,
          label: file.label || 'Untitled',
          url: file.type === 'FILES' ? file.title : file.content || '#',
          icon: file.type === 'FILES' ? 'fas fa-file' : 'fas fa-link',
          description: file.description || '',
          disabled: false,
        }));
      } else if (apiItem.links && Array.isArray(apiItem.links)) {
        item.type = 'links';
        item.links = apiItem.links.map((link: any) => ({
          id: link.accordion_details_id,
          label: link.label || 'Unnamed Link',
          url: link.url || '#',
          description: link.description || '',
          icon: link.icon || 'fas fa-link',
          disabled: link.disabled || false,
        }));
      }

      return item;
    });
  }
  private buildBlobUrl(path: string): string {
    const base = environment.ASSET_BLOB_URL;
    return base + path;
  }
  onPanelOpened(openedItem: AccordionItem): void {
    // Collapse all others at this level
    this.accordionItems.forEach((item) => {
      item.expanded = item === openedItem;
      item.selected = item === openedItem;
    });
    this.itemExpanded.emit(openedItem);
  }

  onPanelClosed(closedItem: AccordionItem): void {
    closedItem.expanded = false;
    closedItem.selected = false;
    if (closedItem.children && closedItem.children.length > 0) {
      this.collapseAllChildren(closedItem.children);
    }

    this.itemCollapsed.emit(closedItem);
  }

  private collapseAllChildren(children: AccordionItem[]): void {
    children.forEach((child) => {
      child.expanded = false;
      child.selected = false;
      if (child.children && child.children.length > 0) {
        this.collapseAllChildren(child.children);
      }
    });
  }

  onMenuAction(action: string, item: AccordionItem, event: Event) {
    this.actionClicked.emit({ action, item });
  }

  onLinkClick(event: Event, link: AccordionLink): void {
    if (link.disabled) {
      event.preventDefault();
      return;
    }

    this.linkClicked.emit(link);

    if (link.url.startsWith('http')) {
      // Open in new tab
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      // Handle internal or special links here
      event.preventDefault();
    }

    event.stopPropagation();
  }

  onLinkMenuAction(action: string, link: AccordionLink) {
    this.actionClicked.emit({
      action,
      item: { id: link.id, title: link.label, type: 'links', data: link },
    });
  }

  // ── Reorder helpers ───────────────────────────────────────────────────────

  isReorderMode(itemId: string): boolean {
    return this.reorderModeMap.get(itemId) ?? false;
  }

  isLoadingReorder(itemId: string): boolean {
    return this.isLoadingMap.get(itemId) ?? false;
  }

  toggleLinkReorderMode(item: AccordionItem, event: Event): void {
    event.stopPropagation();
    const id = item.id!;
    const currentMode = this.isReorderMode(id);

    if (currentMode) {
      // EXIT reorder mode IMMEDIATELY so drag handles disappear right away.
      // This prevents the "icon duplication" seen when the API response
      // arrives and Angular re-renders items while reorder mode was still on.
      this.reorderModeMap.set(id, false);

      if (this.hasLinkOrderChanged(item)) {
        // Show a loading indicator on the button while the API call is in flight.
        // The loading state is cleared automatically when ngOnChanges fires
        // after loadItems() refreshes the accordion items from the server.
        this.isLoadingMap.set(id, true);
        this.filesReordered.emit({ item, links: item.links! });
      }
      // If order did not change, we already exited reorder mode above — nothing else to do.
    } else {
      // ENTER REORDER MODE — save a snapshot of the current order.
      this.initialOrderMap.set(id, (item.links ?? []).map((l) => l.id));
      this.reorderModeMap.set(id, true);
    }
  }

  hasLinkOrderChanged(item: AccordionItem): boolean {
    const initial = this.initialOrderMap.get(item.id!) ?? [];
    const current = (item.links ?? []).map((l) => l.id);
    return JSON.stringify(initial) !== JSON.stringify(current);
  }

  dropLink(event: CdkDragDrop<AccordionLink[]>, item: AccordionItem): void {
    if (!this.reorderModeMap.get(item.id!)) return;
    const links = [...(item.links ?? [])];
    moveItemInArray(links, event.previousIndex, event.currentIndex);
    item.links = links; // new reference → triggers *ngFor re-render
  }

  trackByFn(index: number, item: AccordionItem): string {
    return item.id || `${index}-${item.title}`;
  }

  // Clean trackBy function to prevent icon stacking issues
  trackLinkByFn(index: number, link: AccordionLink): string {
    return link.id ? link.id.toString() : index.toString();
  }
}